import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Burndown / burnup kèm **đường scope** — spec 16.2.
 *
 * Đường scope (tổng công việc) là điểm khác biệt duy nhất giữa biểu đồ này và
 * một burndown thường, và nó là đường quan trọng nhất: không vẽ nó thì scope
 * creep giữa kỳ trông y hệt "đội làm chậm", mà hai thứ đó cần hai cách xử lý
 * hoàn toàn khác nhau.
 *
 * ⚠️ Đếm theo **số task**, không theo story point — `event_log` không ghi point
 * tại từng thời điểm, nên burndown theo point sẽ phải gán point *hiện tại* cho
 * quá khứ. Velocity theo point thì đọc được, vì nó chỉ nói về sprint đã đóng.
 */
export function BurndownChart({
  projectId,
  range,
  sprintId,
}: {
  projectId: string;
  range: ReportRange;
  sprintId?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.burndown(projectId, range, sprintId ?? ''),
    queryFn: () => reportApi.burndown(projectId, range, sprintId),
  });

  const buckets = report.data?.buckets ?? [];

  return (
    <ChartFrame
      title={t('reports.burndownTitle')}
      help="burndown"
      subtitle={report.data?.sprintName ?? t('reports.burndownRange')}
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && buckets.every((bucket) => bucket.total === 0)
          ? { title: t('reports.emptyTitle'), description: t('reports.emptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.remaining'), color: FLOW_VAR.inProgress },
        { label: t('reports.scope'), color: RAG_VAR.red },
        { label: t('reports.doneLine'), color: FLOW_VAR.done },
        { label: t('reports.ideal'), color: RAG_VAR.unknown },
      ]}
    >
      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={buckets}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <Tooltip
              contentStyle={{
                background: 'var(--color-background)',
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            {/* Đường scope vẽ đậm hơn: nó là lý do biểu đồ này tồn tại. */}
            <Line
              type="monotone"
              dataKey="total"
              name={t('reports.scope')}
              stroke={RAG_VAR.red}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="remaining"
              name={t('reports.remaining')}
              stroke={FLOW_VAR.inProgress}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="done"
              name={t('reports.doneLine')}
              stroke={FLOW_VAR.done}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="ideal"
              name={t('reports.ideal')}
              stroke={RAG_VAR.unknown}
              strokeDasharray="4 4"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}
